import { createSlice } from "@reduxjs/toolkit";
import { apiFetch, getFullImageUrl } from "../api";
import { toast } from "react-hot-toast";
import { getFallbackImage } from "../utils/imageLoader";

// Helper function to safely calculate totals within reducers
const calculateTotals = (items) => {
  let total = 0;
  let itemCount = 0;
  items.forEach(item => {
    const price = Number(item.price) || 0;
    const quantity = Number(item.quantity) || 0;
    if (price > 0 && quantity > 0) {
      total += price * quantity;
      itemCount += quantity;
    }
  });
  return { total, itemCount };
};

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    items: [],
    total: 0,
    itemCount: 0,
  },
  reducers: {
    setCart: (state, action) => {
      state.items = action.payload || [];
      const { total, itemCount } = calculateTotals(state.items);
      state.total = total;
      state.itemCount = itemCount;
    },
    addToCart: (state, action) => {
      const productToAdd = action.payload;
      const quantityToAdd = Number(productToAdd.quantity) || 1;
      const existingItem = state.items.find(item => item.id === productToAdd.id);

      if (existingItem) {
        existingItem.quantity = (Number(existingItem.quantity) || 0) + quantityToAdd;
      } else {
        state.items.push({
             ...productToAdd,
             price: Number(productToAdd.price) || 0,
             quantity: quantityToAdd
        });
      }
      const { total, itemCount } = calculateTotals(state.items);
      state.total = total;
      state.itemCount = itemCount;
    },
    removeFromCart: (state, action) => {
      const productIdToRemove = action.payload;
      state.items = state.items.filter(item => item.id !== productIdToRemove);
      const { total, itemCount } = calculateTotals(state.items);
      state.total = total;
      state.itemCount = itemCount;
    },
    updateQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const item = state.items.find(item => item.id === id);
      const newQuantity = Number(quantity) || 0;

      if (item) {
        item.quantity = newQuantity;
        if (item.quantity <= 0) {
            state.items = state.items.filter(i => i.id !== id);
        }
      }
      const { total, itemCount } = calculateTotals(state.items);
      state.total = total;
      state.itemCount = itemCount;
    },
    clearCart: (state) => {
      state.items = [];
      state.total = 0;
      state.itemCount = 0;
    }
  }
});

export const { setCart, addToCart, removeFromCart, updateQuantity, clearCart } = cartSlice.actions;
export default cartSlice.reducer;

// ----------------------------------------------------
// THUNKS FOR ASYNCHRONOUS API CALLS
// ----------------------------------------------------

export const fetchUserCart = () => async (dispatch) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const data = await apiFetch('/api/cart');

    if (!data || !data.cart || !Array.isArray(data.cart.items)) {
      dispatch(setCart([]));
      return;
    }

    const validItems = data.cart.items.filter(item => item.product !== null);

    const transformedData = validItems.map(item => {
        let firstImage = getFallbackImage();
        let allImages = [];

        if (item.product.images && Array.isArray(item.product.images) && item.product.images.length > 0) {
            const firstImageData = item.product.images[0];
            firstImage = getFullImageUrl(typeof firstImageData === 'string' ? firstImageData : firstImageData?.url);
            allImages = item.product.images.map(imgData =>
                getFullImageUrl(typeof imgData === 'string' ? imgData : imgData?.url)
            );
        }

        let currentPrice = item.price;
        if (item.product.variants && Array.isArray(item.product.variants)) {
            const matchingVariant = item.product.variants.find(variant =>
                variant.color === item.selectedColor && variant.size === item.selectedSize
            );
            if (matchingVariant && matchingVariant.price) {
                currentPrice = Number(matchingVariant.price);
            }
        }

        return {
          ...item.product,
          image: firstImage,
          images: allImages,
          price: currentPrice,
          quantity: item.quantity,
          id: item.product._id,
          cartItemId: item._id,
          title: item.product.name,
          description: item.product.description, 
          selectedColor: item.selectedColor,
          selectedSize: item.selectedSize
        };
    });

    dispatch(setCart(transformedData));
  } catch (error) {
    console.error("Error fetching cart:", error);
    dispatch(setCart([]));
  }
};


export const addProductToCart = (product, quantity = 1, options = { showNotification: true }) => async (dispatch, getState) => {
  try {
    if (!localStorage.getItem('token')) {
      dispatch(addToCart({ ...product, quantity }));
      if (options.showNotification) {
        window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'add', message: 'Added to cart', item: product }}));
      }
      return;
    }
    const currentItems = getState().cart.items;
    const existingItem = currentItems.find(item => item.id === product.id);
    if (existingItem) {
      await apiFetch('/api/cart/update', {
        method: 'PUT',
        body: { itemId: existingItem.cartItemId, quantity: existingItem.quantity + quantity }
      });
      await dispatch(fetchUserCart());
      if (options.showNotification) {
        window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'add', message: 'Updated quantity', item: product }}));
      }
    } else {
      await apiFetch('/api/cart/add', {
        method: 'POST',
        body: { product: product.id, quantity, variant: product.variantId, selectedColor: product.selectedColor, selectedSize: product.selectedSize }
      });
      await dispatch(fetchUserCart());
      if (options.showNotification) {
         window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'add', message: 'Added to cart', item: product }}));
      }
    }
  } catch (error) {
    if (options.showNotification) {
      toast.error(error.message || "Failed to add to cart.");
    } else {
      console.error("Silent cart add failed:", error);
    }
  }
};

export const removeProductFromCart = (product) => async (dispatch) => {
    try {
        if (!localStorage.getItem('token')) {
            dispatch(removeFromCart(product.id));
            window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'remove', message: 'Removed from cart', item: product }}));
            return;
        }
        const cartItemId = product.cartItemId;
        if (!cartItemId) {
            console.error("Missing cartItemId for removal.");
            toast.error("Error removing item. Please try again.");
            return;
        }
        await apiFetch(`/api/cart/remove/${cartItemId}`, { method: 'DELETE' });
        dispatch(removeFromCart(product.id));
        window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'remove', message: 'Removed from cart', item: product }}));
    } catch (error) {
        toast.error(error.message || "Failed to remove from cart.");
    }
};

export const updateCartItemQuantity = (product, quantity, options = { showNotification: true }) => async (dispatch) => {
    try {
        if (!localStorage.getItem('token')) {
            dispatch(updateQuantity({ id: product.id, quantity }));
            return;
        }
        if (!product.cartItemId) {
            console.error("Missing cartItemId for update.");
            if (options.showNotification) toast.error("Error updating item. Please try again.");
            return;
        }
        await apiFetch('/api/cart/update', {
            method: 'PUT',
            body: { itemId: product.cartItemId, quantity }
        });
        dispatch(updateQuantity({ id: product.id, quantity }));
    } catch (error) {
        if (options.showNotification) {
            toast.error(error.message || "Failed to update cart item.");
        } else {
            console.error("Silent cart update failed:", error);
        }
    }
};

export const clearUserCart = () => async (dispatch, getState) => {
    try {
        if (!localStorage.getItem('token')) {
            dispatch(clearCart());
            return;
        }
        const currentItems = getState().cart.items;
        for (const item of currentItems) {
            if (item.cartItemId) {
                await apiFetch(`/api/cart/remove/${item.cartItemId}`, { method: 'DELETE' });
            }
        }
        dispatch(clearCart());
        window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'remove', message: 'Cart cleared', item: null }}));
    } catch (error) {
        toast.error(error.message || "Failed to clear cart.");
    }
};